const TRUTHY_VALUES = new Set(['1', 'true', 'on', 'yes']);
const FALSY_VALUES = new Set(['0', 'false', 'off', 'no']);

let cachedBytesOnlyFlag = null;
let cachedDebugLoggingFlag = null;

const normalizeString = (value) => {
  if (value == null) {
    return null;
  }
  try {
    const normalized = String(value).trim();
    return normalized ? normalized : null;
  } catch {
    return null;
  }
};

const parseFlagValue = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  const normalized = normalizeString(value);
  if (!normalized) {
    return null;
  }
  const lower = normalized.toLowerCase();
  if (TRUTHY_VALUES.has(lower)) {
    return true;
  }
  if (FALSY_VALUES.has(lower)) {
    return false;
  }
  return null;
};

const resolveRuntimeScope = () => {
  if (typeof window !== 'undefined') return window;
  try {
    // eslint-disable-next-line no-new-func
    return new Function('return typeof globalThis !== "undefined" ? globalThis : null;')();
  } catch {}
  return null;
};

const readGlobalFlag = (key) => {
  const scope = resolveRuntimeScope();
  if (!scope || !key) {
    return null;
  }
  const sources = [
    scope,
    scope.__MLS_FLAGS__,
    scope.__FEATURE_FLAGS__,
    scope.__APP_FLAGS__,
  ];
  for (const source of sources) {
    if (!source) continue;
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      return source[key];
    }
  }
  return null;
};

const readProcessEnv = (key) => {
  if (!key) {
    return null;
  }
  try {
    if (typeof process !== 'undefined' && process?.env && key in process.env) {
      return process.env[key];
    }
  } catch {}
  return null;
};

const readImportMetaEnv = (key) => {
  if (!key) {
    return null;
  }
  let env = null;
  try {
    // eslint-disable-next-line no-new-func
    env = new Function(
      'return (typeof import.meta !== "undefined" && import.meta && import.meta.env) ? import.meta.env : null;',
    )();
  } catch {}
  if (env && key in env) {
    return env[key];
  }
  return null;
};

const readStorageFlag = (key) => {
  const scope = resolveRuntimeScope();
  if (!key || !scope) {
    return null;
  }
  const storageSources = [scope.localStorage, scope.sessionStorage];
  for (const storage of storageSources) {
    if (!storage || typeof storage.getItem !== 'function') continue;
    try {
      const value = storage.getItem(key);
      if (value != null) return value;
    } catch {}
  }
  return null;
};

const FLAG_KEYS = [
  'MLS_WORKER_BYTES_ONLY',
  'REACT_APP_MLS_WORKER_BYTES_ONLY',
  'VITE_MLS_WORKER_BYTES_ONLY',
];

const DEBUG_LOGGING_FLAG_KEYS = [
  'MESSENGER_V2_DEBUG_LOGS',
  'REACT_APP_MESSENGER_V2_DEBUG_LOGS',
  'VITE_MESSENGER_V2_DEBUG_LOGS',
];

export const isWorkerBytesOnlyEnabled = () => {
  if (cachedBytesOnlyFlag != null) {
    return cachedBytesOnlyFlag;
  }

  for (const key of FLAG_KEYS) {
    const globalValue = parseFlagValue(readGlobalFlag(key));
    if (globalValue != null) {
      cachedBytesOnlyFlag = globalValue;
      return cachedBytesOnlyFlag;
    }
  }

  for (const key of FLAG_KEYS) {
    const envValue = parseFlagValue(readProcessEnv(key));
    if (envValue != null) {
      cachedBytesOnlyFlag = envValue;
      return cachedBytesOnlyFlag;
    }
  }

  for (const key of FLAG_KEYS) {
    const metaValue = parseFlagValue(readImportMetaEnv(key));
    if (metaValue != null) {
      cachedBytesOnlyFlag = metaValue;
      return cachedBytesOnlyFlag;
    }
  }

  cachedBytesOnlyFlag = false;
  return cachedBytesOnlyFlag;
};

export const isMessengerV2DebugLoggingEnabled = () => {
  if (cachedDebugLoggingFlag != null) {
    return cachedDebugLoggingFlag;
  }

  for (const key of DEBUG_LOGGING_FLAG_KEYS) {
    const globalValue = parseFlagValue(readGlobalFlag(key));
    if (globalValue != null) {
      cachedDebugLoggingFlag = globalValue;
      return cachedDebugLoggingFlag;
    }
  }

  for (const key of DEBUG_LOGGING_FLAG_KEYS) {
    const storageValue = parseFlagValue(readStorageFlag(key));
    if (storageValue != null) {
      cachedDebugLoggingFlag = storageValue;
      return cachedDebugLoggingFlag;
    }
  }

  for (const key of DEBUG_LOGGING_FLAG_KEYS) {
    const envValue = parseFlagValue(readProcessEnv(key));
    if (envValue != null) {
      cachedDebugLoggingFlag = envValue;
      return cachedDebugLoggingFlag;
    }
  }

  for (const key of DEBUG_LOGGING_FLAG_KEYS) {
    const metaValue = parseFlagValue(readImportMetaEnv(key));
    if (metaValue != null) {
      cachedDebugLoggingFlag = metaValue;
      return cachedDebugLoggingFlag;
    }
  }

  cachedDebugLoggingFlag = false;
  return cachedDebugLoggingFlag;
};
